// Deck Recipe detail page — public, SEO-indexed.
//
// Header (sidebar + back + “Use this recipe”): `getDeckRecipesSlugRouteNavigationOptions`
// in `DeckRecipesNavigationOptions.jsx`.
//
// Renders the full DeckTemplate document with rich SEO meta and JSON-LD
// (HowTo + Article + BreadcrumbList) so search engines understand each
// recipe as a guide. The "Use this recipe" CTA creates a deck for the
// current user and redirects to /deck?d=<slug>.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { deckTemplatesApi, deckTemplateCategoriesApi } from '@/lib/api';
import { useDeckStore } from '@/store';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import DeckRecipeTemplateFavoriteHeart from '@/components/Deck/DeckRecipeTemplateFavoriteHeart';
import DeckTemplateFramePreview from '@/components/Deck/DeckTemplateFramePreview';
import DeckRecipeCard from '@/components/Deck/DeckRecipeCard';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import { heroChromaFromPaletteFg } from '@/components/Tools/toolDetailHelpers';
import { BORDER_RADIUS } from '@/constants/config';
import {
  buildCategoryLookupMaps,
  enrichTemplateCategory,
  formatRecipeCategoryLabel,
  getColumnMeta,
  getColumnBindKind,
  getColumnBindLabel,
  getColumnSubtitle,
  recipeCategorySlug,
} from '@/utils/deckRecipeCategory';

const BIND_KIND_META = {
  model: { icon: 'cube', label: 'Model' },
  tool:  { icon: 'wand-magic-sparkles', label: 'Tool' },
  agent: { icon: 'user-astronaut', label: 'Agent' },
};

const isWeb = Platform.OS === 'web';
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Normalize seeded `pageCopy` blocks for the `ToolPageCopy` renderer:
 *  - new seed shape uses `section`/`ordered-list`/`unordered-list`/`cards`/`faq`
 *    (mirrors `seed_task_templates_rules.md` section 9.1).
 *  - legacy seed shape used `h2` (title) + `p` (paragraph) — collapse adjacent
 *    pairs into a single `section` block so the renderer picks them up.
 */
function normalizeDeckPageCopy(blocks) {
  if (!Array.isArray(blocks)) return [];
  const supported = new Set(['section', 'ordered-list', 'unordered-list', 'cards', 'faq', 'callout']);

  const isLegacy = blocks.some((b) => b?.type === 'h2' || b?.type === 'p');
  if (!isLegacy) return blocks.filter((b) => b && supported.has(b.type));

  const out = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b) continue;
    if (supported.has(b.type)) { out.push(b); continue; }
    if (b.type === 'h2') {
      const title = b.text || '';
      const paragraphs = [];
      let j = i + 1;
      while (j < blocks.length && blocks[j]?.type === 'p' && blocks[j]?.text) {
        paragraphs.push(blocks[j].text);
        j += 1;
      }
      out.push({ type: 'section', title, copy: paragraphs.join('\n\n') });
      i = j - 1;
    } else if (b.type === 'p' && b.text) {
      out.push({ type: 'section', copy: b.text });
    }
  }
  return out;
}

/**
 * Convert seeded `faq` array (entries `{q, a}` or `{question, answer}`) into the
 * `ToolPageCopy` faq block shape.
 */
function buildFaqBlock(faq, title = 'Frequently asked questions') {
  if (!Array.isArray(faq) || faq.length === 0) return null;
  const items = faq
    .map((row) => {
      if (!row) return null;
      const question = row.question || row.q || row.title;
      const answer = row.answer || row.a || row.copy || row.description;
      if (!question || !answer) return null;
      return { question, answer };
    })
    .filter(Boolean);
  if (items.length === 0) return null;
  return { type: 'faq', title, items };
}

// The phone apps don't ship Deck, so its recipe pages have nowhere to lead.
export default function DeckRecipeDetailRoute() {
  if (IS_TOUCH_NATIVE) return <Redirect href="/" />;
  return <DeckRecipeDetail />;
}

function DeckRecipeDetail() {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const slug = typeof params?.slug === 'string' ? params.slug : (Array.isArray(params?.slug) ? params.slug[0] : '');

  const createDeck = useDeckStore(state => state.actions.createDeck);
  const requireAuth = useRequireAuth();

  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyRelatedSlug, setBusyRelatedSlug] = useState(null);
  const [relatedRecipes, setRelatedRecipes] = useState([]);
  const useRecipeBusyRef = useRef(false);
  const useRelatedBusyRef = useRef(false);

  const pushCreatedDeck = useCallback((deckSlug) => {
    if (deckSlug) {
      router.push({ pathname: '/deck', params: { d: deckSlug } });
    }
  }, [router]);

  useEffect(() => {
    let alive = true;
    if (!slug) {
      setTemplate(null);
      setCategories([]);
      setError(null);
      setRelatedRecipes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [data, catRes] = await Promise.all([
          deckTemplatesApi.getBySlug(slug),
          deckTemplateCategoriesApi.list().catch(() => ({ categories: [] })),
        ]);
        if (!alive) return;
        setTemplate(data?.template || null);
        setCategories(Array.isArray(catRes?.categories) ? catRes.categories : []);
      } catch (err) {
        if (!alive) return;
        setError(err?.status === 404 ? 'Recipe not found' : (err?.message || 'Failed to load recipe'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const categoryMaps = useMemo(() => buildCategoryLookupMaps(categories), [categories]);

  const displayTemplate = useMemo(
    () => (template ? enrichTemplateCategory(template, categoryMaps) : null),
    [template, categoryMaps],
  );

  // ── Related recipes ───────────────────────────────────────────────────
  // Fetch siblings from the same category once we know the category slug.
  // Falls back to the global feed if the category lookup turns up empty,
  // so the section always renders *something* to keep browsing.
  const relatedCategorySlug = useMemo(() => {
    if (!displayTemplate) return null;
    return recipeCategorySlug(displayTemplate) || null;
  }, [displayTemplate]);

  useEffect(() => {
    let alive = true;
    if (!displayTemplate?.slug) { setRelatedRecipes([]); return; }
    (async () => {
      try {
        const opts = { limit: 20 };
        if (relatedCategorySlug && relatedCategorySlug !== 'other') opts.category = relatedCategorySlug;
        let data = await deckTemplatesApi.list(opts).catch(() => null);
        let rows = Array.isArray(data?.templates) ? data.templates : [];
        // If the category-scoped fetch returns nothing (rare — e.g. orphaned
        // recipe), fall back to the global feed so we still surface options.
        if (rows.length <= 1) {
          const allData = await deckTemplatesApi.list({ limit: 30 }).catch(() => null);
          rows = Array.isArray(allData?.templates) ? allData.templates : rows;
        }
        if (!alive) return;
        const enriched = rows
          .filter((r) => r?.slug && r.slug !== displayTemplate.slug)
          .map((r) => enrichTemplateCategory(r, categoryMaps))
          .slice(0, 6);
        setRelatedRecipes(enriched);
      } catch {
        if (alive) setRelatedRecipes([]);
      }
    })();
    return () => { alive = false; };
  }, [displayTemplate?.slug, relatedCategorySlug, categoryMaps]);

  const handleUseRecipe = useCallback(async () => {
    if (!displayTemplate || useRecipeBusyRef.current) return;
    // Creating a deck is owner-only — surface the unified auth CTA for
    // anonymous visitors instead of a hard `/login` redirect.
    const ran = requireAuth(() => true, {
      source: 'deck_recipe_detail_use_recipe',
      title: `Sign in to use ${displayTemplate?.name || 'this recipe'}`,
      subtitle: 'Recipes turn into decks in your library so you can keep iterating on them.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    useRecipeBusyRef.current = true;
    setBusy(true);
    try {
      const deckSlug = await createDeck({ name: displayTemplate.name, templateSlug: displayTemplate.slug });
      pushCreatedDeck(deckSlug);
    } finally {
      useRecipeBusyRef.current = false;
      setBusy(false);
    }
  }, [displayTemplate, requireAuth, createDeck, pushCreatedDeck]);

  const handleUseRelated = useCallback(async (tpl) => {
    if (!tpl?.slug || useRelatedBusyRef.current) return;
    const ran = requireAuth(() => true, {
      source: 'deck_recipe_detail_use_related',
      title: `Sign in to use ${tpl?.name || 'this recipe'}`,
      subtitle: 'Recipes turn into decks in your library so you can keep iterating on them.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    useRelatedBusyRef.current = true;
    setBusyRelatedSlug(tpl.slug);
    try {
      const deckSlug = await createDeck({ name: tpl.name, templateSlug: tpl.slug });
      pushCreatedDeck(deckSlug);
    } finally {
      useRelatedBusyRef.current = false;
      setBusyRelatedSlug(null);
    }
  }, [requireAuth, createDeck, pushCreatedDeck]);

  const handleOpenRelated = useCallback((tpl) => {
    if (tpl?.slug) router.push(`/decks/recipes/${tpl.slug}`);
  }, [router]);

  // ── Computed pageCopy blocks for ToolPageCopy ─────────────────────────
  const pageCopyBlocks = useMemo(() => {
    const blocks = normalizeDeckPageCopy(displayTemplate?.meta?.pageCopy);
    const hasFaqBlock = blocks.some((b) => b?.type === 'faq');
    if (hasFaqBlock) return blocks;
    const faqTitle = displayTemplate?.name ? `${displayTemplate.name} FAQ` : 'Recipe FAQ';
    const faqBlock = buildFaqBlock(displayTemplate?.meta?.faq, faqTitle);
    return faqBlock ? [...blocks, faqBlock] : blocks;
  }, [displayTemplate]);

  const pageCopyPalette = useMemo(() => {
    const accent = colors.accent_color_primary;
    if (!displayTemplate) {
      return { fg: accent, bg: `${accent}1f`, soft: `${accent}0d` };
    }
    const cols = displayTemplate.columns || [];
    const fg = cols[0] ? getColumnMeta(cols[0]).color : accent;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [displayTemplate, colors.accent_color_primary]);

  // Hero wash chroma — same values `CatalogDetailHero` computes from the
  // palette, so the bottom CTA's tint reads identically to the page hero.
  const heroChroma = useMemo(
    () => heroChromaFromPaletteFg(pageCopyPalette.fg),
    [pageCopyPalette.fg],
  );

  // ── SEO payload ────────────────────────────────────────────────────────
  const structuredData = useMemo(() => {
    if (!displayTemplate) return null;
    const url = `https://gab.ai/decks/recipes/${displayTemplate.slug}`;
    const headline = displayTemplate.meta?.title || `${displayTemplate.name} — Gab AI Deck Recipe`;
    const desc = displayTemplate.meta?.description || displayTemplate.description || '';

    const graph = [
      {
        '@type': 'HowTo',
        name: headline,
        description: desc,
        totalTime: 'PT45M',
        tool: [{ '@type': 'HowToTool', name: 'Gab AI' }],
        step: (displayTemplate.columns || []).map((col, i) => {
          const m = getColumnMeta(col);
          return {
            '@type': 'HowToStep',
            position: i + 1,
            name: col.label || `${m.label} step`,
            text: `${col.label || m.label}: run this stage in a dedicated Deck column alongside the rest of your workflow.`,
          };
        }),
        url,
      },
      {
        '@type': 'Article',
        headline,
        description: desc,
        keywords: displayTemplate.meta?.keywords,
        author: { '@type': 'Organization', name: 'Gab AI', url: 'https://gab.ai' },
        publisher: { '@type': 'Organization', name: 'Gab AI' },
        dateModified: displayTemplate.updatedAt,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://gab.ai' },
          { '@type': 'ListItem', position: 2, name: 'Deck',         item: 'https://gab.ai/deck' },
          { '@type': 'ListItem', position: 3, name: 'Deck recipes', item: 'https://gab.ai/decks/recipes' },
          { '@type': 'ListItem', position: 4, name: displayTemplate.name,  item: url },
        ],
      },
    ];

    const faqItems = Array.isArray(displayTemplate.meta?.faq) ? displayTemplate.meta.faq : [];
    if (faqItems.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: faqItems
          .map((item) => {
            const q = item?.q || item?.question;
            const a = item?.a || item?.answer;
            return q && a
              ? {
                  '@type': 'Question',
                  name: q,
                  acceptedAnswer: { '@type': 'Answer', text: a },
                }
              : null;
          })
          .filter(Boolean),
      });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
  }, [displayTemplate]);

  const metaTitle = useMemo(
    () => displayTemplate?.meta?.title || (displayTemplate ? `${displayTemplate.name} — Deck Recipe` : 'Deck Recipe'),
    [displayTemplate],
  );
  const metaDesc = useMemo(
    () => displayTemplate?.meta?.description || displayTemplate?.description || 'A pre-built multi-modal AI workflow on Gab AI.',
    [displayTemplate],
  );
  const metaKeys = useMemo(
    () => displayTemplate?.meta?.keywords || `${displayTemplate?.name || 'deck recipe'}, ai workflow, gab ai`,
    [displayTemplate],
  );

  const { cols, heroGradientA, heroGradientB } = useMemo(() => {
    const c = displayTemplate?.columns || [];
    const first = c[0] ? getColumnMeta(c[0]).color : null;
    const last = c[c.length - 1] ? getColumnMeta(c[c.length - 1]).color : null;
    const accent = colors.accent_color_primary;
    const a = first || accent;
    const b = last || a;
    return { cols: c, heroGradientA: a, heroGradientB: b };
  }, [displayTemplate, colors.accent_color_primary]);

  const recipeHeroIcon = useMemo(() => {
    const c = displayTemplate?.columns || [];
    const m = c[0] ? getColumnMeta(c[0]) : null;
    return m?.icon || 'table-columns';
  }, [displayTemplate]);

  const recipeHeroMetaItems = useMemo(() => {
    if (!displayTemplate) return [];
    const c = displayTemplate.columns || [];
    const rows = [{ icon: 'table-columns', label: `${c.length} column${c.length === 1 ? '' : 's'}` }];
    if (displayTemplate.stats?.usageCount > 0) {
      rows.push({ icon: 'bolt', label: `${displayTemplate.stats.usageCount.toLocaleString()} runs` });
    }
    return rows;
  }, [displayTemplate]);

  const padX = isXS ? 16 : 28;

  return (
    <>
      <MetaHead
        noindex
        title={metaTitle}
        description={metaDesc}
        keywords={metaKeys}
        canonical={displayTemplate ? `/decks/recipes/${displayTemplate.slug}` : '/decks/recipes'}
        structuredData={structuredData || undefined}
        type="article"
      />

      <ScrollView
        style={{ flex: 1, backgroundColor: colors.solid_color_primary }}
        contentContainerStyle={styles.scroll}
      >
        {loading && (
          <View style={{ width: '100%', alignItems: 'center', paddingHorizontal: padX, paddingTop: 28 }}>
            <View style={[styles.wrap, { width: '100%' }]}>
              <View style={styles.loading}>
                <Icon name="spinner" size={20} color={colors.text_color_tertiary} />
              </View>
            </View>
          </View>
        )}

        {!loading && error && (
          <View style={{ width: '100%', alignItems: 'center', paddingHorizontal: padX, paddingTop: 28 }}>
            <View style={styles.wrap}>
              <View style={[styles.errorBox, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                <Text size="m" weight="bold" color="primary">Recipe unavailable</Text>
                <Text size="s" color="secondary" style={{ marginTop: 6 }}>{error}</Text>
                <Pressable
                  onPress={() => router.push('/decks/recipes')}
                  style={({ hovered }) => [styles.errorCta, { borderColor: colors.border_color_secondary, backgroundColor: hovered ? colors.solid_color_hover : 'transparent' }]}
                >
                  <Text size="xs" weight="bold" color="secondary">Browse all recipes</Text>
                  <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
                </Pressable>
              </View>
            </View>
          </View>
        )}

        {!loading && !error && displayTemplate && (
          <>
            <CatalogDetailHero
              backLink={!IS_TOUCH_NATIVE ? {
                label: 'All recipes',
                onPress: () => router.push('/decks/recipes'),
              } : null}
              palette={pageCopyPalette}
              categorySlug={relatedCategorySlug && relatedCategorySlug !== 'other' ? relatedCategorySlug : undefined}
              gradientSlug={displayTemplate?.slug}
              catalogCategory={{
                label: formatRecipeCategoryLabel(displayTemplate),
                icon: 'layer-group',
                ...(relatedCategorySlug && relatedCategorySlug !== 'other'
                  ? {
                      href: `/decks/recipes/category/${encodeURIComponent(relatedCategorySlug)}`,
                    }
                  : {}),
              }}
              headline={displayTemplate.name}
              subtitle={displayTemplate.description || null}
              heroIcon={recipeHeroIcon}
              heroMaxWidth={1100}
              metaItems={recipeHeroMetaItems}
              favoriteAccessory={<DeckRecipeTemplateFavoriteHeart template={displayTemplate} hitSlop={12} />}
              primaryAction={{
                label: busy ? 'Creating deck…' : 'Use this recipe',
                icon: busy ? 'spinner' : 'plus',
                onPress: handleUseRecipe,
                disabled: busy,
                accessibilityLabel: busy ? 'Creating deck' : 'Use this recipe',
              }}
            />

            <View style={[styles.wrap, { paddingHorizontal: padX }]}>
              <View
                style={[
                  styles.deckExplainer,
                  { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
                ]}
              >
                <View style={styles.deckExplainerRow}>
                  <View style={[styles.deckExplainerIcon, { backgroundColor: 'rgba(59,130,246,0.12)' }]}>
                    <Icon name="table-columns" size={14} color="#3b82f6" solid />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xxs" weight="bold" color="tertiary" style={styles.deckExplainerEyebrow}>
                      What’s a deck?
                    </Text>
                    <Text size="s" color="secondary" style={styles.deckExplainerBody}>
                      A deck is Gab AI’s{' '}
                      <Text weight="bold" color="primary">multi-column workspace</Text>
                      {' '}— not one long chat. Each column runs a different model or tool (text, image, video, music, voice, transcription, one-click tools, and more) side by side, all reading the same brief.{' '}
                      <Text weight="bold" color="primary">Recipes</Text>
                      {' '}are pre-wired layouts you clone in one click; rename columns, swap models, or add up to 12 columns whenever you want.
                    </Text>
                  </View>
                </View>
              </View>

              {cols.length > 0 && (
                <View style={styles.deckPreviewWrap}>
                  <DeckTemplateFramePreview
                    template={displayTemplate}
                    isXS={isXS}
                    footerEyebrow="How it looks in Gab AI"
                    footerDescription="Scroll sideways to see every column. Use this recipe above to clone this exact layout into your workspace."
                  />
                </View>
              )}

              {/* Workflow preview frame — rich per-column cards so the
                  recipe page tells the user *exactly* which model / tool /
                  agent each column wires up and what it produces, before
                  they ever click "Use this recipe". */}
              <View style={styles.previewFrame}>
                <View style={[styles.previewStripe, { overflow: 'hidden' }]}>
                  {isWeb ? (
                    <View style={[StyleSheet.absoluteFill, { backgroundImage: `linear-gradient(90deg, ${heroGradientA}, ${heroGradientB})` }]} />
                  ) : (
                    <LinearGradient colors={[heroGradientA, heroGradientB]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
                  )}
                </View>
                <View style={[styles.previewBody, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
                  <View style={styles.previewHeader}>
                    <Text size="xxs" weight="bold" color="tertiary" style={styles.sectionLabel}>
                      THE WORKFLOW · {cols.length} {cols.length === 1 ? 'COLUMN' : 'COLUMNS'}
                    </Text>
                    <Text size="xs" color="secondary" style={styles.previewSub}>
                      Each column runs a different model, tool, or agent — side by side, all reading the same brief.
                    </Text>
                  </View>

                  <View style={styles.steps}>
                    {cols.map((col, i) => {
                      const m = getColumnMeta(col);
                      const bindKind = getColumnBindKind(col);
                      const bindLabel = getColumnBindLabel(col);
                      const subtitle = getColumnSubtitle(col);
                      const bindMeta = bindKind ? BIND_KIND_META[bindKind] : null;
                      const title = col.label || m.label;

                      return (
                        <View
                          key={i}
                          style={[
                            styles.stepCard,
                            {
                              backgroundColor: colors.solid_color_primary,
                              borderColor: colors.border_color_secondary,
                            },
                          ]}
                        >
                          <View style={styles.stepHeader}>
                            <View style={[styles.stepNumber, { backgroundColor: `${m.color}1f`, borderColor: `${m.color}55` }]}>
                              <Text size="xxs" weight="bold" style={{ color: m.color, fontSize: 10 }}>{i + 1}</Text>
                            </View>
                            <View style={[styles.stepIcon, { backgroundColor: `${m.color}1a` }]}>
                              <Icon name={m.icon} size={11} color={m.color} solid />
                            </View>
                            <Text size="xxs" weight="bold" style={[styles.stepKind, { color: m.color }]}>
                              {(m.longLabel || m.label).toUpperCase()}
                            </Text>
                          </View>

                          <Text size="s" weight="bold" color="primary" style={styles.stepLabel}>
                            {title}
                          </Text>

                          {!!subtitle && (
                            <Text size="xs" color="secondary" style={styles.stepSubtitle}>
                              {subtitle}
                            </Text>
                          )}

                          {bindMeta && bindLabel && (
                            <View style={[styles.stepBindPill, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                              <Icon name={bindMeta.icon} size={9} color={colors.text_color_tertiary} solid />
                              <Text size="xxs" weight="bold" color="tertiary" style={styles.stepBindKind}>
                                {bindMeta.label.toUpperCase()}
                              </Text>
                              <View style={[styles.stepBindDivider, { backgroundColor: colors.border_color_secondary }]} />
                              <Text size="xxs" weight="bold" color="secondary" numberOfLines={1} style={{ flex: 1, minWidth: 0 }}>
                                {bindLabel}
                              </Text>
                            </View>
                          )}
                          {!bindMeta && (
                            <View style={[styles.stepBindPill, styles.stepBindPillBlank, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                              <Icon name="plus" size={9} color={colors.text_color_tertiary} solid />
                              <Text size="xxs" weight="bold" color="tertiary" style={{ marginLeft: 6 }}>
                                Pick a {m.label.toLowerCase()} after cloning
                              </Text>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </View>
              </View>

              {/* Editor-written SEO body — backed by `meta.pageCopy` (typed
                  blocks that mirror Tool/Task templates) plus an auto-appended
                  FAQ block if `meta.faq` is set. Renders via `ToolPageCopy`. */}
              {(pageCopyBlocks.length > 0) && (
                <View style={styles.pageCopyWrap}>
                  <ToolPageCopy blocks={pageCopyBlocks} palette={pageCopyPalette} isMD={false} />
                </View>
              )}

              {/* Fallback if legacy templates have no pageCopy */}
              {(!displayTemplate.meta?.pageCopy || displayTemplate.meta.pageCopy.length === 0) && (
              <View style={styles.copyBlock}>
                <Text size="xs" weight="bold" color="tertiary" style={styles.sectionLabel}>
                  ABOUT THIS RECIPE
                </Text>
                <Text size="m" color="secondary" style={styles.copyText}>
                  <Text size="m" weight="bold" color="primary">{displayTemplate.name}</Text>{' '}
                  is a pre-built deck recipe on Gab AI. It opens {cols.length} columns side by side, each running a different model or tool, so you can move from{' '}
                  <Text size="m" color="primary" weight="medium">{cols[0]?.label || 'the first step'}</Text>{' '}
                  to{' '}
                  <Text size="m" color="primary" weight="medium">{cols[cols.length - 1]?.label || 'the final output'}</Text>{' '}
                  without leaving the workspace.
                </Text>
                <Text size="m" color="secondary" style={[styles.copyText, { marginTop: 12 }]}>
                  Click <Text size="m" weight="bold" color="primary">Use this recipe</Text> and Gab AI creates a new deck with this column layout. Rename it, attach models, add chat or tool columns, and reorder—up to 12 columns per deck.
                </Text>
              </View>
              )}

              {/* Related recipes — same category first, fall back to the
                  global feed if the category is sparse. Closes the loop after
                  a long recipe page so the user keeps browsing instead of
                  bouncing back to the catalog. */}
              {relatedRecipes.length > 0 && (
                <View style={styles.relatedBlock}>
                  <View style={styles.relatedHeader}>
                    <View>
                      <Text size="xxs" weight="bold" color="tertiary" style={styles.sectionLabel}>
                        RELATED RECIPES
                      </Text>
                      <Text size="m" weight="bold" color="primary" style={styles.relatedTitle}>
                        More like {displayTemplate.name}
                      </Text>
                    </View>
                    <Pressable
                      onPress={() => {
                        if (relatedCategorySlug && relatedCategorySlug !== 'other') {
                          router.push(`/decks/recipes/category/${relatedCategorySlug}`);
                        } else {
                          router.push('/decks/recipes');
                        }
                      }}
                      style={({ hovered }) => [
                        styles.relatedAll,
                        { borderColor: colors.border_color_secondary, backgroundColor: hovered ? colors.solid_color_hover : 'transparent' },
                      ]}
                    >
                      <Text size="xxs" weight="bold" color="secondary">See all</Text>
                      <Icon name="arrow-right" size={9} color={colors.text_color_secondary} solid />
                    </Pressable>
                  </View>

                  <View style={styles.relatedGrid}>
                    {relatedRecipes.map((tpl) => (
                      <View key={tpl.slug} style={styles.relatedCell}>
                        <DeckRecipeCard
                          template={tpl}
                          onPress={() => handleOpenRelated(tpl)}
                          onUse={() => handleUseRelated(tpl)}
                          busy={busyRelatedSlug === tpl.slug}
                          categoryFallbackSlug={relatedCategorySlug || undefined}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Final CTA — centered hero-language card with a soft
                  accent wash (matches `CatalogDetailHero` chroma) and a
                  filled pill button. */}
              <View
                style={[
                  styles.bottomCta,
                  {
                    borderColor: colors.border_color_secondary,
                    backgroundColor: colors.solid_color_block,
                  },
                ]}
              >
                <LinearGradient
                  colors={[colors.solid_color_tertiary, 'transparent']}
                  locations={[0, 1]}
                  start={{ x: 0.5, y: 1 }}
                  end={{ x: 0.5, y: 0.2 }}
                  style={styles.bottomCtaWashSoft}
                  pointerEvents="none"
                />
                <LinearGradient
                  colors={[heroChroma.gradSoft, heroChroma.gradMid, 'transparent']}
                  locations={[0, 0.2, 1]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.bottomCtaWashSoft}
                  pointerEvents="none"
                />
                <View
                  style={[
                    styles.bottomCtaBlobA,
                    isXS && styles.bottomCtaBlobAMobile,
                    { backgroundColor: heroChroma.blobA },
                  ]}
                  pointerEvents="none"
                />
                <View
                  style={[
                    styles.bottomCtaBlobB,
                    isXS && styles.bottomCtaBlobBMobile,
                    { backgroundColor: heroChroma.blobB },
                  ]}
                  pointerEvents="none"
                />
                <View style={[styles.bottomCtaInner, isXS && styles.bottomCtaInnerMobile]}>
                  <View
                    style={[
                      styles.bottomCtaBadge,
                      {
                        borderColor: `${heroGradientA}55`,
                        backgroundColor: `${heroGradientA}1a`,
                      },
                    ]}
                  >
                    <Icon name="layer-group" size={11} color={heroGradientA} solid />
                    <Text
                      size="s"
                      weight="extraBold"
                      style={{ color: heroGradientA, letterSpacing: 0.6 }}
                    >
                      {String(formatRecipeCategoryLabel(displayTemplate) || 'Deck recipe').toUpperCase()}
                    </Text>
                  </View>
                  <Text
                    tag="h2"
                    color="primary"
                    style={[styles.bottomCtaTitle, isXS && styles.bottomCtaTitleMobile]}
                  >
                    Ready to run {displayTemplate.name}?
                  </Text>
                  <Text
                    color="secondary"
                    style={[styles.bottomCtaBody, isXS && styles.bottomCtaBodyMobile]}
                  >
                    We&apos;ll create the deck for you and open it instantly.
                  </Text>
                  <Pressable
                    onPress={handleUseRecipe}
                    disabled={busy}
                    accessibilityRole="button"
                    accessibilityLabel={busy ? 'Creating deck' : 'Use this recipe'}
                    style={({ hovered, pressed }) => [
                      styles.bottomCtaButton,
                      { backgroundColor: heroGradientA, opacity: busy ? 0.75 : 1 },
                      // NOTE: avoid `transform: translateY` on hover. RN Web's
                      // Pressable hover state lands on the shifted bounds; with
                      // the mouse right at the boundary, hoverOut → translate
                      // reverts → hoverIn loops fast enough to trip React's
                      // "Maximum update depth" guard. Box-shadow alone gives
                      // the lift without moving the hit area.
                      Platform.OS === 'web' && hovered && !busy && {
                        boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                      },
                      pressed && !busy && { opacity: 0.92 },
                    ]}
                  >
                    <Icon name={busy ? 'spinner' : 'plus'} size={16} color="#fff" solid />
                    <Text size="m" weight="bold" style={{ color: '#fff' }}>
                      {busy ? 'Creating deck…' : 'Use this recipe'}
                    </Text>
                    <Icon name="arrow-right" size={12} color="#fff" solid />
                  </Pressable>
                </View>
              </View>

              <AppDashboardFooter />
            </View>
          </>
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  scroll: { flexGrow: 1, paddingBottom: 80, alignItems: 'center', width: '100%' },
  wrap: { width: '100%', maxWidth: 1100 },

  loading: { paddingVertical: 80, alignItems: 'center' },

  errorBox: {
    padding: 24,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
    alignItems: 'flex-start',
  },
  errorCta: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 6, borderWidth: 1, marginTop: 14,
  },

  deckExplainer: {
    marginTop: 24,
    padding: 14,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
    maxWidth: 760,
    alignSelf: 'stretch',
  },
  deckExplainerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  deckExplainerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  deckExplainerEyebrow: {
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  deckExplainerBody: {
    lineHeight: 22,
  },
  deckPreviewWrap: {
    width: '100%',
    marginTop: 18,
    marginBottom: 8,
  },

  previewFrame: { borderRadius: BORDER_RADIUS.large, overflow: 'hidden', marginTop: 12 },
  previewStripe: { height: 4, position: 'relative' },
  previewBody: { padding: 20, borderWidth: 1, borderTopWidth: 0, borderBottomLeftRadius: BORDER_RADIUS.large, borderBottomRightRadius: BORDER_RADIUS.large },
  previewHeader: { marginBottom: 4 },
  previewSub: { marginTop: 6, lineHeight: 18, maxWidth: 580 },

  sectionLabel: { textTransform: 'uppercase', letterSpacing: 1.2 },

  steps: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 16 },
  stepCard: {
    flexBasis: 260, flexGrow: 1, maxWidth: 360,
    padding: 14, borderRadius: BORDER_RADIUS.medium, borderWidth: 1,
    gap: 10,
  },
  stepHeader: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepNumber: {
    width: 22, height: 22, borderRadius: 11, borderWidth: 1,
    alignItems: 'center', justifyContent: 'center',
  },
  stepIcon: { width: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  stepKind: { fontSize: 9, letterSpacing: 1.4, marginLeft: 'auto' },
  stepLabel: { marginTop: 2, fontSize: 14, lineHeight: 18 },
  stepSubtitle: { lineHeight: 18 },
  stepBindPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  stepBindPillBlank: { opacity: 0.85 },
  stepBindKind: { fontSize: 9, letterSpacing: 1.2 },
  stepBindDivider: { width: 1, height: 10, opacity: 0.5 },

  relatedBlock: { marginTop: 40 },
  relatedHeader: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 18,
  },
  relatedTitle: { marginTop: 6, letterSpacing: -0.2 },
  relatedAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    ...(isWeb ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
  relatedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    ...(isWeb ? { alignItems: 'stretch' } : {}),
  },
  relatedCell: {
    flexBasis: 260,
    flexGrow: 1,
    maxWidth: 360,
    minWidth: 0,
    ...(isWeb ? { display: 'flex', flexDirection: 'column' } : {}),
  },

  copyBlock: { marginTop: 36 },
  copyText: { marginTop: 12, lineHeight: 26, maxWidth: 760 },

  pageCopyWrap: {
    marginTop: 36,
    width: '100%',
    maxWidth: 820,
    alignSelf: 'stretch',
  },

  // "Ready to run X?" — centered hero-language CTA card. The blob /
  // gradient values are computed from `heroChromaFromPaletteFg`
  // (matches `CatalogDetailHero` exactly). Inline styles in the JSX
  // supply the per-recipe colors so the static StyleSheet stays cheap.
  bottomCta: {
    width: '100%',
    marginTop: 36,
    borderRadius: BORDER_RADIUS.large,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  bottomCtaWashSoft: { ...StyleSheet.absoluteFillObject },
  bottomCtaBlobA: {
    position: 'absolute',
    width: 360,
    height: 360,
    borderRadius: 400,
    top: -118,
    right: -88,
    opacity: isWeb ? 0.52 : 0.5,
    ...(isWeb ? { filter: 'blur(44px)' } : null),
  },
  bottomCtaBlobAMobile: {
    width: 280,
    height: 280,
    top: -100,
    right: -72,
  },
  bottomCtaBlobB: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 300,
    bottom: -130,
    left: -90,
    opacity: isWeb ? 0.38 : 0.36,
    ...(isWeb ? { filter: 'blur(36px)' } : null),
  },
  bottomCtaBlobBMobile: {
    width: 200,
    height: 200,
    bottom: -110,
    left: -70,
  },
  bottomCtaInner: {
    padding: 44,
    gap: 12,
    alignItems: 'center',
  },
  bottomCtaInnerMobile: {
    padding: 28,
  },
  bottomCtaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 11,
    borderRadius: 999,
    borderWidth: 1,
    marginBottom: 6,
  },
  bottomCtaTitle: {
    fontSize: 38,
    lineHeight: 44,
    letterSpacing: -0.6,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 0,
  },
  bottomCtaTitleMobile: {
    fontSize: 28,
    lineHeight: 32,
  },
  bottomCtaBody: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    maxWidth: 540,
    opacity: 0.78,
  },
  bottomCtaBodyMobile: {
    fontSize: 15,
    lineHeight: 22,
  },
  bottomCtaButton: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 26,
    borderRadius: 999,
    ...(isWeb
      ? {
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          transition: 'box-shadow 0.2s ease, background-color 0.15s ease',
        }
      : null),
  },
});
